import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useSocket from "./useSocket";
import {
  clearDuelsActiveMatch,
  setDuelsActiveMatch,
} from "../features/duelsActiveMatch/activeMatchSlice";

/**
 * Routes where `active-match` should be subscribed.
 * Must cover everywhere users can be during a join / match flow:
 * - Host may be on lobby, create room, room details, or game chat.
 * - Guest who sent a join request is often on `/view-duels-rooms` (not under `/duels`).
 * Outside these paths the listener stays off (home, profile, marketplace, etc.).
 */
export function isActiveMatchListenerRoute(pathname) {
  return Boolean(pathname);
}

const normalizeActiveMatchPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if ("room" in payload || "isBusy" in payload || "canSubmitScore" in payload) {
    return payload;
  }
  return {
    room: payload,
    isBusy: payload?.isBusy ?? true,
  };
};

/**
 * Single app-wide subscription to `active-match`, toggled by `enabled`.
 * Call only from Layout (or one parent) so the socket handler is not duplicated.
 */
export default function useActiveMatchListener(enabled) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, isReady } = useSocket();

  const navigateToGameRoom = useCallback(
    (rawPayload) => {
      const eventData = normalizeActiveMatchPayload(rawPayload);
      const roomData = eventData?.room;
      const roomId = roomData?._id || null;
      const shouldOpenScoreSubmission =
        eventData?.canSubmitScore === true && eventData?.isBusy === false;

      if (shouldOpenScoreSubmission) {
        const scorePath = "/one-to-one-score-submission";
        if (location.pathname === scorePath) return;
        navigate(scorePath, {
          state: {
            data: eventData || {},
          },
        });
        return;
      }

      if (roomId && eventData?.isBusy) {
        const roomPath = `/one-to-one-message/${roomId}`;
        if (location.pathname === roomPath) return;
        navigate(roomPath, {
          state: {
            data: eventData || {},
          },
        });
      }
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    if (!enabled || !socket || !isReady) {
      if (!enabled) dispatch(clearDuelsActiveMatch());
      return;
    }

    const handleActiveMatch = (eventData) => {
      const normalizedData = normalizeActiveMatchPayload(eventData);
      console.log('active-match eventData: ', eventData);
      navigateToGameRoom(normalizedData);
      dispatch(setDuelsActiveMatch(normalizedData));
    };

    socket.on("active-match", handleActiveMatch);
    return () => {
      socket.off("active-match", handleActiveMatch);
    };
  }, [
    enabled,
    socket,
    isReady,
    dispatch,
    navigateToGameRoom,
  ]);
}
